import React, { useRef, useMemo, useImperativeHandle, useContext } from 'react';
import { Text as DreiText } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const DEFAULT_FONT_URL = '/fonts/montserrat-v15-latin-regular.ttf';
const LOCAL_FALLBACK_FONTS = [
  { src: '/fonts/noto-fallback/NotoSans-Regular.ttf' },
  { src: '/fonts/noto-fallback/NotoSansHebrew-Regular.ttf', unicodeRange: 'U+0590-05FF,U+FB1D-FB4F' },
  {
    src: '/fonts/noto-fallback/NotoSansSymbols2-Regular.ttf',
    unicodeRange: 'U+2000-206F,U+2190-21FF,U+2200-22FF,U+2300-23FF,U+2460-24FF,U+2500-257F,U+25A0-25FF,U+2600-26FF,U+2700-27BF,U+2B00-2BFF'
  }
];

const normalizeFontStack = (font) => {
  const primaryFonts = Array.isArray(font) ? font : [typeof font === 'string' ? { src: font } : font || { src: DEFAULT_FONT_URL }];
  const seen = new Set();
  const merged = [];

  primaryFonts.concat(LOCAL_FALLBACK_FONTS).forEach((entry) => {
    if (!entry?.src || seen.has(entry.src)) return;
    seen.add(entry.src);
    merged.push(entry);
  });

  return merged;
};

const Text3DLodContext = React.createContext({
  enabled: false,
  hideDistance: 90
});

export function Text3DLodProvider({ enabled = true, hideDistance = 90, children }) {
  const value = useMemo(() => ({ enabled, hideDistance }), [enabled, hideDistance]);

  return <Text3DLodContext.Provider value={value}>{children}</Text3DLodContext.Provider>;
}
const SafeTroikaText = React.forwardRef(({ children, text, font = DEFAULT_FONT_URL, camera: cameraProp, lod, hideDistance, ...props }, forwardedRef) => {
  const innerRef = useRef();
  const cameraFromThree = useThree((state) => state.camera);
  const camera = cameraProp || cameraFromThree;
  const lodContext = useContext(Text3DLodContext);

  const resolvedLod = lod ?? lodContext.enabled;
  const resolvedHideDistance = hideDistance ?? lodContext.hideDistance ?? 90;
  useImperativeHandle(forwardedRef, () => innerRef.current);

  const worldPosition = useMemo(() => new THREE.Vector3(), []);

  const resolvedFonts = useMemo(() => normalizeFontStack(font), [font]);
  const primaryFont = resolvedFonts[0]?.src || DEFAULT_FONT_URL;
  const resolvedChildren = text == null ? children : String(text);

  useFrame(() => {
    const obj = innerRef.current;
    if (!obj || !camera) return;

    if (!resolvedLod) {
      obj.visible = true;
      return;
    }

    obj.getWorldPosition(worldPosition);
    const distance = camera.position.distanceTo(worldPosition);

    obj.visible = distance < resolvedHideDistance;
  });

  return (
    <DreiText ref={innerRef} {...props} font={primaryFont} fonts={resolvedFonts}>
      {resolvedChildren}
    </DreiText>
  );
});
SafeTroikaText.displayName = 'SafeTroikaText';

export const Text3D = SafeTroikaText;
export const Text = SafeTroikaText;

Text3D.displayName = 'Text3D';
Text.displayName = 'Text';
